import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  ArrowLeft, 
  Search, 
  Car, 
  Loader2,
  Calendar,
  Fuel,
  Palette,
  Users,
  Gauge,
  Globe,
  Edit3,
  AlertCircle,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTRIES = [
  { code: "NL", name: "Nederland", badge: "NL", bg: "bg-blue-700", text: "text-white" },
  { code: "BE", name: "België", badge: "BE", bg: "bg-black", text: "text-yellow-400" },
  { code: "DE", name: "Duitsland", badge: "DE", bg: "bg-black", text: "text-yellow-400" },
  { code: "FR", name: "Frankrijk", badge: "FR", bg: "bg-blue-600", text: "text-white" },
  { code: "GB", name: "Verenigd Koninkrijk", badge: "GB", bg: "bg-blue-900", text: "text-white" },
  { code: "OTHER", name: "Anders", badge: "INT", bg: "bg-gray-600", text: "text-white" },
];

const NewInspectionPage = () => {
  const [kenteken, setKenteken] = useState("");
  const [vehicleData, setVehicleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [recentInspection, setRecentInspection] = useState(null);
  const [manualData, setManualData] = useState({
    land: "",
    merk: "",
    handelsbenaming: "",
    kleur: "",
    brandstof: "",
    bouwjaar: ""
  });
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const formatKentekenInput = (value) => {
    // Remove all non-alphanumeric characters
    const cleaned = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    return cleaned.slice(0, 10);
  };

  const handleKentekenChange = (e) => {
    const formatted = formatKentekenInput(e.target.value);
    setKenteken(formatted);
    setVehicleData(null);
    setNotFound(false);
    setManualMode(false);
    setRecentInspection(null);
  };

  const handleSearch = async () => {
    if (kenteken.length < 4) {
      toast.error("Voer een geldig kenteken in");
      return;
    }

    setLoading(true);
    setNotFound(false);
    setRecentInspection(null);
    
    try {
      // First check for recent inspections
      try {
        const recentCheck = await axios.get(`${API}/inspections/check/${kenteken}`, {
          headers: getAuthHeaders()
        });
        if (recentCheck.data.has_recent) {
          setRecentInspection(recentCheck.data);
        }
      } catch (e) {
        // Ignore errors in recent check
      }
      
      // Then fetch RDW data
      const response = await axios.get(`${API}/rdw/${kenteken}`, {
        headers: getAuthHeaders()
      });
      setVehicleData(response.data);
      setManualMode(false);
      toast.success("Voertuig gevonden!");
    } catch (error) {
      if (error.response?.status === 404) {
        setNotFound(true);
        toast.error("Voertuig niet gevonden in RDW database", {
          description: "Kies een land en vul de gegevens handmatig in"
        });
      } else {
        const message = error.response?.data?.detail || "Fout bij zoeken";
        toast.error(message);
      }
      setVehicleData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleManualEntry = () => {
    setManualMode(true);
    setManualData({
      land: "",
      merk: "",
      handelsbenaming: "",
      kleur: "",
      brandstof: "",
      bouwjaar: ""
    });
  };

  const handleStartInspection = async () => {
    setCreating(true);
    try {
      let payload = { kenteken };
      
      if (manualMode) {
        // Validate manual data
        if (!manualData.land || !manualData.merk) {
          toast.error("Vul minimaal land en merk in");
          setCreating(false);
          return;
        }
        payload.manual_vehicle = {
          kenteken: kenteken,
          merk: manualData.merk.toUpperCase(),
          handelsbenaming: manualData.handelsbenaming,
          kleur: manualData.kleur.toUpperCase(),
          brandstof: manualData.brandstof,
          land: manualData.land,
          bouwjaar: manualData.bouwjaar
        };
      }
      
      const response = await axios.post(`${API}/inspections`, payload, {
        headers: getAuthHeaders()
      });
      toast.success("Keuring gestart!");
      navigate(`/inspection/${response.data.id}`);
    } catch (error) {
      toast.error("Kon keuring niet starten");
    } finally {
      setCreating(false);
    }
  };

  const canStartInspection = vehicleData || (manualMode && manualData.land && manualData.merk);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate("/")}
              className="text-white hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading font-black text-xl">Nieuwe Keuring</h1>
              <p className="text-white/70 text-sm">Voer kenteken in om te beginnen</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-md mx-auto px-4 py-8 pb-32">
        {/* Kenteken Input Card */}
        <Card className="mb-6 animate-slide-up" data-testid="kenteken-card">
          <CardHeader className="pb-4">
            <CardTitle className="font-heading text-xl text-center">
              Kenteken Invoeren
            </CardTitle>
            <CardDescription className="text-center">
              Zoek voertuiggegevens op in RDW database
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <input
                type="text"
                value={kenteken}
                onChange={handleKentekenChange}
                placeholder="KENTEKEN"
                className="license-plate-input w-full"
                maxLength={10}
                data-testid="kenteken-input"
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
            
            <Button 
              className="w-full h-14 bg-primary hover:bg-mechanic-blue-dark text-white font-bold shadow-lg active:scale-95 transition-all"
              onClick={handleSearch}
              disabled={loading || kenteken.length < 4}
              data-testid="search-button"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Search className="w-5 h-5 mr-2" />
              )}
              RDW Opzoeken
            </Button>
          </CardContent>
        </Card>

        {/* Recent Inspection Warning */}
        {recentInspection && (
          <Card className="mb-6 border-2 border-blue-200 bg-blue-50 animate-slide-up" data-testid="recent-inspection-card">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  recentInspection.latest_status === 'approved' ? 'bg-green-100' : 
                  recentInspection.latest_status === 'rejected' ? 'bg-red-100' : 'bg-yellow-100'
                }`}>
                  {recentInspection.latest_status === 'approved' ? (
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  ) : recentInspection.latest_status === 'rejected' ? (
                    <XCircle className="w-6 h-6 text-red-600" />
                  ) : (
                    <Clock className="w-6 h-6 text-yellow-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-lg text-blue-800">
                    Recente keuring gevonden
                  </h3>
                  <p className="text-blue-700 text-sm mt-1">
                    Dit voertuig is <strong>{recentInspection.count}x</strong> gekeurd in de afgelopen 12 maanden.
                  </p>
                  <div className="mt-3 p-3 bg-white rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Laatste keuring</p>
                        <p className="font-semibold">{recentInspection.latest_date}</p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                        recentInspection.latest_status === 'approved' ? 'bg-green-100 text-green-700' :
                        recentInspection.latest_status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {recentInspection.latest_status === 'approved' ? 'Goedgekeurd' :
                         recentInspection.latest_status === 'rejected' ? 'Afgekeurd' : 'In behandeling'}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/inspection/${recentInspection.latest_id}`)}
                      data-testid="view-recent-button"
                    >
                      Bekijk keuring
                    </Button>
                  </div>
                  <p className="text-xs text-blue-600 mt-3">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    Je kunt nog steeds een nieuwe keuring starten
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Not Found - Manual Entry Option */}
        {notFound && !manualMode && (
          <Card className="mb-6 border-2 border-orange-200 bg-orange-50 animate-slide-up" data-testid="not-found-card">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-lg text-orange-800">
                    Niet gevonden in RDW
                  </h3>
                  <p className="text-orange-700 text-sm mt-1 mb-4">
                    Dit kenteken staat niet in de Nederlandse RDW database. 
                    Mogelijk is het een buitenlands voertuig.
                  </p>
                  <Button
                    onClick={handleManualEntry}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="manual-entry-button"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Handmatig Invoeren
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Manual Entry Form */}
        {manualMode && (
          <Card className="mb-6 animate-slide-up" data-testid="manual-form-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                Handmatige Invoer
              </CardTitle>
              <CardDescription>
                Kenteken: <span className="font-mono font-bold">{kenteken}</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="land">Land van herkomst *</Label>
                <Select
                  value={manualData.land}
                  onValueChange={(value) => setManualData(prev => ({ ...prev, land: value }))}
                >
                  <SelectTrigger data-testid="country-select">
                    <SelectValue placeholder="Selecteer land" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(country => (
                      <SelectItem key={country.code} value={country.code}>
                        <span className="flex items-center gap-2">
                          <span className={`${country.bg} ${country.text} px-1.5 py-0.5 rounded text-xs font-bold`}>
                            {country.badge}
                          </span>
                          <span>{country.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="merk">Merk *</Label>
                <Input
                  id="merk"
                  value={manualData.merk}
                  onChange={(e) => setManualData(prev => ({ ...prev, merk: e.target.value }))}
                  placeholder="Bijv. Volkswagen, BMW, Peugeot"
                  className="h-12"
                  data-testid="manual-merk-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="model">Model</Label>
                <Input
                  id="model"
                  value={manualData.handelsbenaming}
                  onChange={(e) => setManualData(prev => ({ ...prev, handelsbenaming: e.target.value }))}
                  placeholder="Bijv. Golf, 3 Serie, 308"
                  className="h-12"
                  data-testid="manual-model-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="kleur">Kleur</Label>
                  <Input
                    id="kleur"
                    value={manualData.kleur}
                    onChange={(e) => setManualData(prev => ({ ...prev, kleur: e.target.value }))}
                    placeholder="Bijv. Zwart"
                    className="h-12"
                    data-testid="manual-kleur-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bouwjaar">Bouwjaar</Label>
                  <Input
                    id="bouwjaar"
                    value={manualData.bouwjaar}
                    onChange={(e) => setManualData(prev => ({ ...prev, bouwjaar: e.target.value }))}
                    placeholder="Bijv. 2019"
                    className="h-12"
                    data-testid="manual-bouwjaar-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="brandstof">Brandstof</Label>
                <Select
                  value={manualData.brandstof}
                  onValueChange={(value) => setManualData(prev => ({ ...prev, brandstof: value }))}
                >
                  <SelectTrigger data-testid="fuel-select">
                    <SelectValue placeholder="Selecteer brandstof" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Benzine">Benzine</SelectItem>
                    <SelectItem value="Diesel">Diesel</SelectItem>
                    <SelectItem value="Elektrisch">Elektrisch</SelectItem>
                    <SelectItem value="Hybride">Hybride</SelectItem>
                    <SelectItem value="LPG">LPG</SelectItem>
                    <SelectItem value="CNG">CNG</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Vehicle Data Card (from RDW) */}
        {vehicleData && (
          <Card className="animate-slide-up" data-testid="vehicle-data-card">
            <CardHeader className="bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-2xl">
                    {vehicleData.merk}
                  </CardTitle>
                  <p className="text-white/70">{vehicleData.handelsbenaming}</p>
                </div>
                <div className="bg-license-yellow border-2 border-black rounded px-3 py-1">
                  <span className="font-mono font-bold text-sm tracking-wider text-black">
                    {vehicleData.kenteken}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                <InfoRow 
                  icon={<Car className="w-5 h-5" />}
                  label="Type"
                  value={vehicleData.voertuigsoort}
                />
                <InfoRow 
                  icon={<Palette className="w-5 h-5" />}
                  label="Kleur"
                  value={vehicleData.kleur}
                />
                <InfoRow 
                  icon={<Fuel className="w-5 h-5" />}
                  label="Brandstof"
                  value={vehicleData.brandstof}
                />
                <InfoRow 
                  icon={<Calendar className="w-5 h-5" />}
                  label="Eerste toelating"
                  value={vehicleData.eerste_toelating}
                />
                <InfoRow 
                  icon={<Calendar className="w-5 h-5" />}
                  label="APK vervalt"
                  value={vehicleData.vervaldatum_apk}
                  highlight={true}
                />
                <InfoRow 
                  icon={<Users className="w-5 h-5" />}
                  label="Zitplaatsen"
                  value={vehicleData.aantal_zitplaatsen}
                />
                <InfoRow 
                  icon={<Gauge className="w-5 h-5" />}
                  label="Massa rijklaar"
                  value={vehicleData.massa_rijklaar ? `${vehicleData.massa_rijklaar} kg` : null}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Manual Vehicle Summary */}
        {manualMode && manualData.land && manualData.merk && (() => {
          const country = COUNTRIES.find(c => c.code === manualData.land);
          return (
            <Card className="mt-6 animate-slide-up" data-testid="manual-summary-card">
              <CardHeader className="bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-2xl">
                      {manualData.merk.toUpperCase()}
                    </CardTitle>
                    <p className="text-white/70">{manualData.handelsbenaming || "Model onbekend"}</p>
                  </div>
                  <div className="flex items-stretch border-2 border-black rounded overflow-hidden">
                    <div className={`${country?.bg} ${country?.text} px-2 flex items-center text-xs font-bold`}>
                      {country?.badge}
                    </div>
                    <div className="bg-white px-3 py-1 flex items-center">
                      <span className="font-mono font-bold text-sm tracking-wider text-black">
                        {kenteken}
                      </span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {manualData.kleur && (
                    <div>
                      <p className="text-muted-foreground">Kleur</p>
                      <p className="font-semibold">{manualData.kleur}</p>
                    </div>
                  )}
                  {manualData.brandstof && (
                    <div>
                      <p className="text-muted-foreground">Brandstof</p>
                      <p className="font-semibold">{manualData.brandstof}</p>
                    </div>
                  )}
                  {manualData.bouwjaar && (
                    <div>
                      <p className="text-muted-foreground">Bouwjaar</p>
                      <p className="font-semibold">{manualData.bouwjaar}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground">Land</p>
                    <p className="font-semibold">{country?.name}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}
      </main>

      {/* Bottom Action Button */}
      {canStartInspection && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-lg border-t safe-bottom z-50">
          <div className="max-w-md mx-auto">
            <Button 
              className="w-full h-16 bg-safety-orange hover:bg-safety-orange-dark text-white font-bold text-lg shadow-2xl shadow-orange-500/40 rounded-xl active:scale-95 transition-all"
              onClick={handleStartInspection}
              disabled={creating}
              data-testid="start-inspection-button"
            >
              {creating ? (
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
              ) : (
                <Car className="w-6 h-6 mr-2" />
              )}
              Start Keuring
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoRow = ({ icon, label, value, highlight }) => {
  if (!value) return null;
  
  return (
    <div className={`flex items-center gap-4 p-4 ${highlight ? 'bg-yellow-50' : ''}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${highlight ? 'bg-yellow-100 text-yellow-700' : 'bg-muted text-muted-foreground'}`}>
        {icon}
      </div>
      <div className="flex-1">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className={`font-semibold ${highlight ? 'text-yellow-700' : ''}`}>{value}</p>
      </div>
    </div>
  );
};

export default NewInspectionPage;
